import React, { useState } from 'react';
import { Mail, Phone, Linkedin, Instagram, MessageCircle, Send } from 'lucide-react';
import { personalInfo } from '../../mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFormStatus({ 
          type: 'success', 
          message: 'Message sent successfully! I\'ll get back to you soon.' 
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        setFormStatus({ 
          type: 'error', 
          message: data.message || 'Something went wrong. Please try again.' 
        });
      }
    } catch (error) {
      setFormStatus({ 
        type: 'error', 
        message: 'Failed to send message. Please try again later.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 px-6 bg-[#1A1430]/30">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-center mb-4">Let's build something that works</h2>
        <p className="text-center text-[#9D97B5] text-lg mb-12">
          Got a project or just want to talk strategy? I reply fast.
        </p>
        
        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Phone */}
          <a
            href={`tel:${personalInfo.phone}`}
            className="flex items-center gap-4 p-6 bg-[#1A1430] border border-[#7C3AED]/10 rounded-2xl hover:border-[#7C3AED]/30 transition-all duration-300 group"
          >
            <div className="w-12 h-12 rounded-full bg-[#7C3AED]/10 flex items-center justify-center group-hover:bg-[#7C3AED]/20 transition-colors">
              <Phone size={24} className="text-[#7C3AED]" />
            </div>
            <div>
              <p className="text-sm text-[#9D97B5] mb-1">Phone</p>
              <p className="text-lg font-semibold text-[#EDE9FE] group-hover:text-[#7C3AED] transition-colors">
                {personalInfo.phone}
              </p>
            </div>
          </a>
          
          {/* Email */}
          <a
            href={`mailto:${personalInfo.email}`}
            className="flex items-center gap-4 p-6 bg-[#1A1430] border border-[#7C3AED]/10 rounded-2xl hover:border-[#7C3AED]/30 transition-all duration-300 group"
          >
            <div className="w-12 h-12 rounded-full bg-[#7C3AED]/10 flex items-center justify-center group-hover:bg-[#7C3AED]/20 transition-colors">
              <Mail size={24} className="text-[#7C3AED]" />
            </div>
            <div>
              <p className="text-sm text-[#9D97B5] mb-1">Email</p>
              <p className="text-lg font-semibold text-[#EDE9FE] group-hover:text-[#7C3AED] transition-colors break-all">
                {personalInfo.email}
              </p>
            </div>
          </a>
        </div>
        
        {/* Contact Form */}
        <div className="bg-[#1A1430] border border-[#7C3AED]/10 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your Name"
                required
                className="w-full px-6 py-4 bg-[#0E0B1F] border border-[#7C3AED]/10 rounded-xl text-[#EDE9FE] placeholder-[#9D97B5] focus:outline-none focus:border-[#7C3AED] transition-colors"
              />
            </div>
            
            <div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Your Email"
                required
                className="w-full px-6 py-4 bg-[#0E0B1F] border border-[#7C3AED]/10 rounded-xl text-[#EDE9FE] placeholder-[#9D97B5] focus:outline-none focus:border-[#7C3AED] transition-colors"
              />
            </div>
            
            <div>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Your Message"
                rows="5"
                required
                className="w-full px-6 py-4 bg-[#0E0B1F] border border-[#7C3AED]/10 rounded-xl text-[#EDE9FE] placeholder-[#9D97B5] focus:outline-none focus:border-[#7C3AED] resize-none transition-colors"
              />
            </div>
            
            {/* Status Message */}
            {formStatus.message && (
              <div className={`p-4 rounded-xl ${
                formStatus.type === 'success' 
                  ? 'bg-[#22D3A5]/10 border border-[#22D3A5]/20 text-[#22D3A5]' 
                  : 'bg-[#FF5C39]/10 border border-[#FF5C39]/20 text-[#FF5C39]'
              }`}>
                {formStatus.message}
              </div>
            )}
            
            {/* Submit Button - Coral Accent */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-[#FF5C39] text-white rounded-full font-semibold text-lg hover:bg-[#E54D2E] transition-all duration-300 hover:shadow-lg hover:shadow-[#FF5C39]/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? 'Sending...' : (
                <>
                  Send it
                  <Send size={20} />
                </>
              )}
            </button>
          </form>
        </div>
        
        {/* Social Links */}
        <div className="flex gap-6 justify-center mt-12">
          <a
            href="https://linkedin.com/in/maheen-kehkasha-8a6a9a245"
            target="_blank"
            rel="noopener noreferrer"
            className="w-12 h-12 rounded-full bg-[#1A1430] border border-[#7C3AED]/10 flex items-center justify-center text-[#9D97B5] hover:text-[#7C3AED] hover:border-[#7C3AED]/30 transition-all duration-300"
            aria-label="LinkedIn"
          >
            <Linkedin size={20} />
          </a>
          <a
            href="https://instagram.com/maheenkehkasha"
            target="_blank"
            rel="noopener noreferrer"
            className="w-12 h-12 rounded-full bg-[#1A1430] border border-[#7C3AED]/10 flex items-center justify-center text-[#9D97B5] hover:text-[#7C3AED] hover:border-[#7C3AED]/30 transition-all duration-300"
            aria-label="Instagram"
          >
            <Instagram size={20} />
          </a>
          <a
            href={personalInfo.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-12 h-12 rounded-full bg-[#1A1430] border border-[#7C3AED]/10 flex items-center justify-center text-[#9D97B5] hover:text-[#22D3A5] hover:border-[#22D3A5]/30 transition-all duration-300"
            aria-label="WhatsApp"
          >
            <MessageCircle size={20} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
